import * as React from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Header from "./components/Header";
import HeroList, { HeroListItem } from "./components/HeroList";
import Progress from "./components/Progress";
import Login from "./pages/Login";
import Signup from "./pages/SignUp";
import AppMain from "./pages/AppMain";
import Validation from "./pages/Validation"
import MainAppBar from "./components/AppBar";
import apiService from "./services/ApiService";

import SignUpInvitation from "./pages/subpages/SignUpInvitationOrganization";
import CreateOrganization from "./pages/subpages/CreateOrganization";
import ReportForgottenPassword from "./pages/subpages/ForgottenPassword";
import ResetPassword from "./pages/subpages/ResetPassword";
import SignUpSharedModel from "./pages/subpages/SignUpInvitationSharedModel";
import BackOfficeInvitation from "./pages/subpages/SignUpInvitationBackOffice";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { getCookie, setCookie } from "./services/Cookies";
import ErrorBoundary from "./shared/components/ErrorBoundary";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// Feature flag — set to true to re-enable public signup flow (route /signup,
// Register button on Login, "Back to sign up" link on Validation, and the
// `?page=SignUp` URL redirect). Invitation flows remain unaffected.
const SIGNUP_ENABLED = false;

export interface AppProps {
  title: string;
  render_first_page: boolean;
}

let globalGoToLoginPage = null;

// Guard component that redirects to /login if not authenticated
function RequireAuth({ children }: { children: React.ReactElement }) {
  const navigate = useNavigate();
  const [checked, setChecked] = React.useState(false);

  React.useEffect(() => {
    if (!getCookie("accessToken")) {
      navigate('/login', { replace: true });
    } else {
      setChecked(true);
    }
  }, []);

  if (!checked) return null;
  return children;
}

// Authentication wrapper component that handles URL params
function AuthWrapper() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    const accessToken = searchParams.get('access_token');
    const tokenType = searchParams.get('token_type');
    const context = searchParams.get('context');
    const page = searchParams.get('page');

    // Handle special page param
    if (page === 'SignUp' && SIGNUP_ENABLED) {
      searchParams.delete('page');
      setSearchParams(searchParams);
      navigate('/signup', { replace: true });
      return;
    }

    // Handle invitation/reset password contexts — preserve query params
    if (accessToken && tokenType && context) {
      const qs = searchParams.toString();
      if (context === 'invitation') {
        navigate(`/signup/invitation?${qs}`, { replace: true });
      } else if (context === 'reset_password') {
        navigate(`/reset-password?${qs}`, { replace: true });
      } else if (context === 'shared_model') {
        navigate(`/signup/shared-model?${qs}`, { replace: true });
      } else if (context === 'BO_invitation' || context === 'BO_invitation_organization') {
        navigate(`/signup/backoffice?${qs}`, { replace: true });
      }
      return;
    }

    // Default routing based on authentication
    if (getCookie("accessToken")) {
      navigate('/app/models', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  }, []);

  return null;
}

export default class App extends React.Component<AppProps, {}> {
  constructor(props, context) {
    super(props, context);
  }

  render() {
    const { title } = this.props;

    return (
      <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Auth wrapper for initial routing */}
          <Route path="/" element={<AuthWrapper />} />
          
          {/* Authentication routes */}
          <Route path="/login" element={<LoginWrapper />} />
          {SIGNUP_ENABLED && <Route path="/signup" element={<SignupWrapper />} />}
          <Route path="/validation" element={<ValidationWrapper />} />
          <Route path="/signup/invitation" element={<SignUpInvitationWrapper />} />
          <Route path="/signup/shared-model" element={<SignUpSharedModelWrapper />} />
          <Route path="/signup/backoffice" element={<BackOfficeInvitationWrapper />} />
          <Route path="/create-organization" element={<CreateOrganizationWrapper />} />
          <Route path="/forgot-password" element={<ReportForgottenPasswordWrapper />} />
          <Route path="/reset-password" element={<ResetPasswordWrapper />} />
          
          {/* Main app routes (authenticated) */}
          <Route path="/app/*" element={<ErrorBoundary><RequireAuth><AppMain /></RequireAuth></ErrorBoundary>} />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      {process.env.NODE_ENV === "development" && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    );
  }
}

// Wrapper components to handle navigation
function LoginWrapper() {
  const navigate = useNavigate();
  
  globalGoToLoginPage = () => navigate('/login');
  
  return (
    <Login
      login_succeeds={() => navigate('/app/models')}
      on_click_register={SIGNUP_ENABLED ? () => navigate('/signup') : undefined}
      login_to_validation={() => navigate('/validation')}
      report_forgotten_password={() => navigate('/forgot-password')}
    />
  );
}

function SignupWrapper() {
  const navigate = useNavigate();
  return (
    <Signup 
      return_to_login={() => navigate('/login')} 
      register_succeeds={() => navigate('/validation')}
    />
  );
}

function ValidationWrapper() {
  const navigate = useNavigate();
  return (
    <Validation
      validation_succeeds={() => navigate('/app/models')}
      back_to_register={SIGNUP_ENABLED ? () => navigate('/signup') : undefined}
    />
  );
}

function SignUpInvitationWrapper() {
  const navigate = useNavigate();
  return (
    <SignUpInvitation 
      register_succeeds={() => navigate('/app/models')} 
      forbidden_page={() => navigate('/login')}
    />
  );
}

function SignUpSharedModelWrapper() {
  const navigate = useNavigate();
  return (
    <SignUpSharedModel 
      register_succeeds={() => navigate('/app/models')} 
      forbidden_page={() => navigate('/login')}
    />
  );
}

function CreateOrganizationWrapper() {
  const navigate = useNavigate();
  return (
    <CreateOrganization 
      create_succeeds={() => navigate('/app/models')}
    />
  );
}

function ReportForgottenPasswordWrapper() {
  const navigate = useNavigate();
  return (
    <ReportForgottenPassword 
      back_to_login={() => navigate('/login')}
    />
  );
}

function ResetPasswordWrapper() {
  const navigate = useNavigate();
  return (
    <ResetPassword 
      reset_succeeds={() => navigate('/login')} 
      forbidden_page={() => navigate('/login')}
    />
  );
}

function BackOfficeInvitationWrapper() {
  const navigate = useNavigate();
  return (
    <BackOfficeInvitation 
      register_succeeds={() => navigate('/app/models')} 
      register_to_organization={() => navigate('/create-organization')} 
      forbidden_page={() => navigate('/login')}
    />
  );
}

export { globalGoToLoginPage }